import { FormLayout, Remark, useDownload } from '@careinfo/layout';
import { tw } from '@careinfo/tailwind';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { type Table } from '@tanstack/react-table';
import moment from 'moment';
import { ClipboardEvent, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import APIS from '../../constant/EndPoint';
import DownloadIcon from '../../Icons/DownloadIcon';
import { createCombineOrderValidationForm } from '../../utils/validationSchemas';
import Card from '../Reusable/Card';
import ComboboxInput from '../Reusable/ComboboxInput';
import DataTable from '../Reusable/DataTable';
import Modal from '../Reusable/Modal';
import { getManifestItemColumn } from '../Reusable/TableColumns/ManifestTableColumns';
import TextField from '../Reusable/TextInput';
import usePatchHook from '../../customHooks/usePatchHook';
import Spinner from '../Reusable/Spinner';

const EditManifest = (props: any) => {
  const {
    handleEdit,
    isLoading,
    data,
    handleAttachConsignment,
    id,
    isRemoved,
    handleRemove,
  } = props;

  const { mutateAsync: setMarkAsDone, isLoading: isMarkAsDoneLoading } =
    usePatchHook({
      queryKey: ['combine-order', `combine-order-${id}`],
      navigateURL: '',
    });

  const { download } = useDownload();
  const [searchParams, setSearchParams] = useSearchParams({
    limit: '10',
    page: '0',
    searchQuery: '',
  });

  const [openReviewModal, setOpenReviewModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState('');
  const [showMarkAsDoneModal, setShowMarkAsDoneModal] = useState(false);
  const selectedOrders = searchParams.get('selectedOrders');

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    watch,
    setValue,
    getValues,
    clearErrors,
  } = useForm<any>({
    resolver: yupResolver(createCombineOrderValidationForm),
    defaultValues: async () => {
      return {
        driver: {
          id: data?.driver?.id,
          name: data?.driver?.user?.full_name,
        },
        destination: data.destination,
        note: data?.note,
      };
    },
  });

  const handleKeyDown = (event: any) => {
    if (event.key === 'Enter' && event.target.value?.length > 0) {
      handleAttachConsignment(event.target.value, setError);
      event.target.value = '';
    }
  };
  const checkedAllItems = searchParams.get('selectAllItems') === '1';

  const handleIndividualSelect = useCallback(
    (value: string, order: string) => {
      const clonedParams = new URLSearchParams(searchParams);
      const selectedItems = clonedParams.get('selectedItems')?.split(',');
      const selectedOrders = clonedParams.get('selectedOrders')?.split(',');
      if (
        !selectedItems ||
        !selectedItems === undefined ||
        !selectedOrders ||
        !selectedItems === undefined
      ) {
        clonedParams.set('selectedItems', value);
        clonedParams.set('selectedOrders', order);
        setSearchParams(clonedParams);
        return;
      }
      const itemIndex = selectedItems?.findIndex((item) => item === value);
      const orderIndex = selectedOrders?.findIndex((item) => item === order);
      if (itemIndex > -1 || orderIndex > -1) {
        selectedItems.splice(itemIndex, 1);
        selectedOrders.splice(itemIndex, 1);
        const newSelectedItems = selectedItems
          ?.map((item) => item)
          .join(',')
          .toString();
        const newSelectedOrders = selectedOrders
          ?.map((item) => item)
          .join(',')
          .toString();
        if (newSelectedItems.length || newSelectedOrders.length) {
          clonedParams.set('selectedItems', newSelectedItems);
          clonedParams.set('selectedOrders', newSelectedOrders);
        } else {
          clonedParams.delete('selectedItems');
          clonedParams.delete('selectedOrders');
        }
        clonedParams.set('selectAllItems', '0');
      } else {
        selectedItems?.push(value);
        selectedOrders?.push(order);
        const newSelectedItems = selectedItems
          ?.map((item) => item)
          .join(',')
          .toString();
        const newSelectedOrders = selectedOrders
          ?.map((item) => item)
          .join(',')
          .toString();
        clonedParams.set('selectedItems', newSelectedItems);
        clonedParams.set('selectedOrders', newSelectedOrders);
      }
      setSearchParams(clonedParams);
    },
    [searchParams, setSearchParams]
  );

  const isSelected = useCallback(
    (value: string, order: string) => {
      const selectedItems = searchParams.get('selectedItems')?.split(',');
      const selectedOrders = searchParams.get('selectedOrders')?.split(',');
      if (!selectedItems && !selectedOrders) {
        return false;
      }
      return selectedItems?.includes(value) || selectedOrders?.includes(order);
    },
    [searchParams]
  );

  const handleSelectAll = useCallback(
    (table: Table<any>) => {
      const clonedParams = new URLSearchParams(searchParams);
      const {
        options: { data: tableData },
      } = table;
      const isSelected = clonedParams.get('selectAllItems');
      if (isSelected === '1') {
        clonedParams.set('selectAllItems', '0');
        clonedParams.delete('selectedItems');
        clonedParams.delete('selectedOrders');
        setSearchParams(clonedParams);
      } else {
        const selectedItems = tableData
          ?.map((item: any) => item.id)
          .join(',')
          .toString();
        const selectedOrders = tableData
          ?.map((item: any) => item?.order?.id ?? item.id)
          .join(',')
          .toString();
        clonedParams.set('selectAllItems', '1');
        clonedParams.set('selectedItems', selectedItems);
        clonedParams.set('selectedOrders', selectedOrders);
        setSearchParams(clonedParams);
      }
    },
    [searchParams, setSearchParams]
  );

  const manifestColumns = useCallback(
    () =>
      getManifestItemColumn({
        checkedAllItems,
        handleSelectAll,
        isSelected,
        handleIndividualSelect,
        setSelectedOrder,
        setOpenReviewModal,
      }),
    [
      checkedAllItems,
      handleSelectAll,
      isSelected,
      handleIndividualSelect,
      setSelectedOrder,
      setOpenReviewModal,
    ]
  )();

  const preventCopyPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    toast.error('Copy and paste is not allowed here');
  };

  const handleOrderDetailNavigate = () => {
    console.info('');
  };

  const toggleMarkAsDone = async () => {
    const url = `${APIS.COMBINE_ORDERS}${id}/`;
    let formData = {
      is_active: false,
    };
    try {
      const res = await setMarkAsDone({ url, formData, setError });
    } catch (err) {
      if (err instanceof Error) toast.error(err.message);
    } finally {
      setShowMarkAsDoneModal(false);
    }
  };

  const handleDownloadExcel = async () => {
    if (!selectedOrders) {
      toast.error('Please select a order to continue');
      return;
    }
    try {
      await download(
        `logisticservice/api/order/download-excel/`,
        `manifest-order-${moment().format('MMMM-Do-YYYY-h-mm-ss')}.xlsx`,
        selectedOrders
      );
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <>
      <FormLayout
        handleSubmit={handleSubmit}
        handleFormSubmit={handleEdit}
        setError={setError}
        buttonLabel={'Update'}
        isLoading={isLoading}
        disabled={!data?.is_active}
      >
        <div className={tw`mx-4`}>
          <div className={tw`grid grid-cols-2 gap-x-4 `}>
            <ComboboxInput
              url={APIS.DRIVER}
              queryKey={'driver'}
              register={register('Driver')}
              errors={errors?.driver}
              name={'driver'}
              label={'Driver'}
              props={{
                is_active: true,
              }}
              setValue={setValue}
              placeholder={'Search Driver'}
              getValues={getValues}
              clearErrors={clearErrors}
            />
            <TextField
              register={register('destination')}
              errors={errors.destination}
              label={'Transport'}
            />
          </div>
          <TextField
            register={register('note')}
            errors={errors.note}
            label={'Note'}
          />
        </div>
      </FormLayout>

      <Card title='Barcode Scanner'>
        <>
          <div className={tw`flex flex-col  items-end w-full`}>
            <div className={tw`grid grid-cols-1 w-full`}>
              <div>
                <input
                  type='text'
                  {...register('tracking_number')}
                  disabled={!data.is_active}
                  className={tw`w-full focus:outline-none bg-[#EEF5F4] p-2 mt-1`}
                  onPaste={preventCopyPaste}
                  onKeyDown={handleKeyDown}
                />
                {errors?.tracking_number && (
                  <p className={tw`text-red-500 text-xs my-1`}>
                    {errors?.tracking_number?.message}
                  </p>
                )}
              </div>
            </div>
          </div>
        </>
      </Card>

      <DataTable
        url={`${APIS.COMBINE_ORDER_ITEMS}`}
        queryKey={'combine-order-items'}
        columns={manifestColumns}
        params={{ manifest: data?.id, order_by: '-modified' }}
        tableName={'combine-order-items'}
        handleDetailNavigate={handleOrderDetailNavigate}
        actionMenu={
          <div className={tw`w-full text-right flex items-center`}>
            <button
              className={tw`flex gap-x-1 cursor-pointer mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1`}
              onClick={handleDownloadExcel}
            >
              <DownloadIcon color={'var(--primary)'} />
              Download
            </button>
            {data?.is_active ? (
              <button
                className={tw`p-1 px-5 rounded-sm border border-primary mr-2  focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1`}
                onClick={() => setShowMarkAsDoneModal(!showMarkAsDoneModal)}
              >
                Mark As Done
              </button>
            ) : null}
            {data?.is_active ? (
              <button
                className={tw`p-1 px-5  rounded-sm border border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1`}
                disabled={isRemoved}
                onClick={() => handleRemove()}
              >
                {isRemoved ? <Spinner /> : 'Remove'}
              </button>
            ) : null}
          </div>
        }
      />
      <Modal
        isOpen={openReviewModal}
        setIsOpen={setOpenReviewModal}
        title={'Remark'}
      >
        <div className={tw`w-[600px]`}>
          <Remark selectedOrder={selectedOrder} />
        </div>
      </Modal>
      <Modal
        isOpen={showMarkAsDoneModal}
        setIsOpen={setShowMarkAsDoneModal}
        showTitle={false}
      >
        <div className={tw`grid place-items-center w-fit`}>
          <div className={tw`space-y-3`}>
            <h2 className={tw`text-xl font-bold text-center`}>Are you sure?</h2>
            <div className={tw`flex items-center gap-5 justify-end`}>
              <button
                className={tw`bg-gray-200 rounded-lg px-2 py-2 focus:outline-none`}
                onClick={() => setShowMarkAsDoneModal(false)}
              >
                Cancel
              </button>
              <button
                onClick={toggleMarkAsDone}
                className={tw`px-2 py-2 rounded-lg bg-primary  text-white disabled:bg-primary-light`}
                disabled={isMarkAsDoneLoading}
              >
                <p>Mark As done</p>
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default EditManifest;
